import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalClients: 0,
    pendingIssues: 0
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      await Promise.all([fetchStats(), fetchProjects()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { count: totalProjects } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true });

      const { count: activeProjects } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .neq('status', 'completed');

      const { count: totalClients } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'client');

      const { count: pendingIssues } = await supabase
        .from('issues')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      setStats({
        totalProjects: totalProjects || 0,
        activeProjects: activeProjects || 0,
        totalClients: totalClients || 0,
        pendingIssues: pendingIssues || 0
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching stats:', error);
      }
    }
  };

  const fetchProjects = async () => {
    try {
      const { data } = await supabase
        .from('projects')
        .select(`
          *,
          users:client_id (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });
      setProjects(data || []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching projects:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'discovery': 'bg-purple-100 text-purple-800',
      'design': 'bg-blue-100 text-blue-800',
      'development': 'bg-yellow-100 text-yellow-800',
      'testing': 'bg-orange-100 text-orange-800',
      'deployment': 'bg-green-100 text-green-800',
      'completed': 'bg-green-200 text-green-900',
      'on-hold': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Manage all projects and clients</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-sm font-medium text-gray-600">Total Projects</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.totalProjects}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-sm font-medium text-gray-600">Active Projects</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{stats.activeProjects}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-sm font-medium text-gray-600">Total Clients</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{stats.totalClients}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-sm font-medium text-gray-600">Pending Issues</div>
          <div className="text-3xl font-bold text-orange-600 mt-2">{stats.pendingIssues}</div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/projects/new')}
            className="px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-left"
          >
            <div className="font-semibold">+ Create New Project</div>
            <div className="text-sm opacity-80 mt-1">Start a new client project</div>
          </button>
          <button
            onClick={() => navigate('/admin/clients/new')}
            className="px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-left"
          >
            <div className="font-semibold">+ Add New Client</div>
            <div className="text-sm opacity-80 mt-1">Create a client account</div>
          </button>
          <button
            onClick={() => navigate('/admin/projects')}
            className="px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-left"
          >
            <div className="font-semibold">View All Projects</div>
            <div className="text-sm opacity-80 mt-1">Manage existing projects</div>
          </button>
        </div>
      </div>

      {/* Projects Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">All Projects</h2>
        </div>

        {projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600">No projects yet. Create your first project to get started.</p>
            <button
              onClick={() => navigate('/admin/projects/new')}
              className="mt-4 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Create Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/admin/project/${project.id}`)}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer overflow-hidden group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-black transition-colors">
                      {project.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full font-semibold ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>

                  {project.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>{project.users?.full_name || project.users?.email || 'No client'}</span>
                    </div>

                    {project.current_phase && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span>{project.current_phase}</span>
                      </div>
                    )}

                    {project.next_milestone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">{project.next_milestone}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button className="text-sm font-semibold text-black hover:text-gray-700 transition-colors flex items-center gap-1">
                      View Project
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
